import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { formatPhoneDisplay } from '@/lib/utils'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Phone, PhoneIncoming, PhoneOutgoing, MessageSquare, Star,
  Clock, Check, Undo2, StickyNote, PhoneCall, Trash2, Bell, ChevronDown, ChevronUp, Loader2,
  Calendar as CalendarIcon, ExternalLink,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import api from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import type { CalendarBooking } from './BookingsCalendar'

interface BookingDetailPanelProps {
  booking: CalendarBooking | null
  open: boolean
  onClose: () => void
  onToggleHandled: (booking: CalendarBooking) => void
  onEditNote: (booking: CalendarBooking) => void
  onDelete: (booking: CalendarBooking) => void
  onToggleReminder: (booking: CalendarBooking, field: 'attorneyReminderEnabled' | 'clientConfirmationEnabled', value: boolean) => void
}

// Format vague times nicely: "morning" → "in the morning", "1 pm" stays as-is
function formatScheduledTime(time: string | null | undefined): string | null {
  if (!time || time === 'to be confirmed') return null
  const lower = time.toLowerCase().trim()
  if (lower === 'morning') return 'in the morning'
  if (lower === 'afternoon') return 'in the afternoon'
  if (lower === 'evening') return 'in the evening'
  // Capitalize AM/PM properly
  return time.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase())
}

function formatAppointmentDisplay(booking: CalendarBooking): string | null {
  const parts: string[] = []

  if (booking.scheduledDate) {
    if (/^\d{4}-\d{2}-\d{2}/.test(booking.scheduledDate)) {
      parts.push(format(new Date(booking.scheduledDate + 'T12:00:00'), 'EEEE, MMM do'))
    } else {
      parts.push(booking.scheduledDate)
    }
  }

  const timeDisplay = formatScheduledTime(booking.scheduledTime)
  if (timeDisplay) {
    parts.push(timeDisplay)
  }

  if (parts.length === 0) return null
  // "Wednesday, Apr 8 in the morning" or "Wednesday, Apr 8 at 1 PM"
  if (parts.length === 2) {
    const isVague = ['in the morning', 'in the afternoon', 'in the evening'].includes(parts[1])
    return `${parts[0]} ${isVague ? '' : 'at '}${parts[1]}`
  }
  return parts[0]
}

export default function BookingDetailPanel({
  booking, open, onClose, onToggleHandled, onEditNote, onDelete, onToggleReminder,
}: BookingDetailPanelProps) {
  // Test confirmation call state — declare before any conditional return
  // so hooks are called unconditionally (Rules of Hooks).
  const [testCallFiring, setTestCallFiring] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { toast } = useToast()
  const { agent } = useAuth()
  // Gate the test-call button to admins/supervisors. Matches the backend
  // route's role check at POST /api/voice/bookings/test-confirmation-call.
  // Platform super-admins also see it (they're admin within whatever org
  // they're impersonating).
  const canFireTestCall = agent?.role === 'admin' || agent?.role === 'supervisor'

  if (!booking) return null

  const sourceIcon = booking.type === 'calendar'
    ? <CalendarIcon className="h-4 w-4 text-violet-500" />
    : booking.source === 'Inbound Call'
    ? <PhoneIncoming className="h-4 w-4 text-brand-royal" />
    : booking.source === 'Outbound Call'
      ? <PhoneOutgoing className="h-4 w-4 text-brand-periwinkle" />
      : <MessageSquare className="h-4 w-4 text-brand-indigo" />

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return null
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const appointmentDisplay = formatAppointmentDisplay(booking)

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full max-w-[100vw] sm:w-[540px] p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-5 pt-5 pb-4 border-b space-y-0">
          <div className="flex items-center gap-2">
            {sourceIcon}
            <SheetTitle className="text-base">
              {booking.callerName || (booking.phone ? formatPhoneDisplay(booking.phone) : 'Unknown')}
            </SheetTitle>
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Badge variant="outline" className="text-[11px]">{booking.source}</Badge>
            {booking.addressed ? (
              <Badge className="text-[11px] bg-brand-royal/15 text-brand-royal border-brand-royal/30 hover:bg-brand-royal/20">
                <Check className="h-2.5 w-2.5 mr-0.5" />Handled
              </Badge>
            ) : (
              <Badge className="text-[11px] bg-amber-500/15 text-amber-600 border-amber-500/30">
                Needs Attention
              </Badge>
            )}
            {booking.caseType && (
              <Badge variant="secondary" className="text-[11px] bg-brand-ice text-brand-indigo dark:bg-brand-deep dark:text-brand-periwinkle">
                {booking.caseType}
              </Badge>
            )}
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Quick action card */}
          {(appointmentDisplay || booking.phone) && (
            <div className="bg-brand-ice/50 dark:bg-brand-deep/30 rounded-lg p-3.5 space-y-2">
              {appointmentDisplay && (
                <div className="flex items-center gap-2.5">
                  <Clock className="h-4 w-4 text-brand-royal shrink-0" />
                  <span className="text-sm font-semibold text-brand-royal">{appointmentDisplay}</span>
                </div>
              )}
              {booking.phone && (
                <div className="flex items-center gap-2.5">
                  <Phone className="h-4 w-4 text-brand-royal shrink-0" />
                  <a href={`tel:${booking.phone}`} className="text-sm font-semibold text-brand-royal hover:underline">
                    {formatPhoneDisplay(booking.phone)}
                  </a>
                </div>
              )}
              {booking.scheduledTime === 'to be confirmed' && (
                <p className="text-xs text-amber-600 ml-6.5">Time to be confirmed</p>
              )}
            </div>
          )}

          {/* Summary */}
          {booking.summary && (
            <div>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Summary</h4>
              <p className="text-sm text-foreground/90 leading-relaxed">{booking.summary}</p>
            </div>
          )}

          {/* Details */}
          <div>
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Details</h4>
            <div className="space-y-2.5">
              <DetailRow
                icon={<Clock className="h-3.5 w-3.5" />}
                label={booking.isExternalCalendarEvent ? 'Scheduled' : 'Call Received'}
                value={format(new Date(booking.date), 'MMM d, yyyy \'at\' h:mm a')}
              />
              {booking.calendarEventLocation && (
                <DetailRow
                  icon={<CalendarIcon className="h-3.5 w-3.5" />}
                  label="Location"
                  value={booking.calendarEventLocation}
                />
              )}
              {booking.duration && (
                <DetailRow
                  icon={<PhoneCall className="h-3.5 w-3.5" />}
                  label="Duration"
                  value={formatDuration(booking.duration)}
                />
              )}
              {booking.qualificationScore && (
                <DetailRow
                  icon={<Star className="h-3.5 w-3.5" />}
                  label="Qualification"
                  value={
                    <span className="flex items-center gap-1">
                      <span className="font-medium">{booking.qualificationScore}</span>
                      <span className="text-muted-foreground">/ 10</span>
                    </span>
                  }
                />
              )}
              {booking.sentiment && (
                <DetailRow
                  icon={<span className="h-3.5 w-3.5 flex items-center justify-center text-xs">
                    {booking.sentiment.toLowerCase() === 'positive' ? '+' : booking.sentiment.toLowerCase() === 'negative' ? '−' : '~'}
                  </span>}
                  label="Sentiment"
                  value={
                    <span className={
                      booking.sentiment.toLowerCase() === 'positive' ? 'text-emerald-500 capitalize' :
                      booking.sentiment.toLowerCase() === 'negative' ? 'text-red-500 capitalize' : 'text-muted-foreground capitalize'
                    }>
                      {booking.sentiment.toLowerCase()}
                    </span>
                  }
                />
              )}
            </div>
          </div>

          {/* Reminders */}
          {(booking.scheduledDate || booking.scheduledTime) && booking.type !== 'task' && !booking.isExternalCalendarEvent && (
            <div>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Reminders</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bell className="h-3.5 w-3.5 text-muted-foreground" />
                    <Label htmlFor="attorney-reminder" className="text-sm cursor-pointer">
                      Remind me before
                    </Label>
                  </div>
                  <Switch
                    id="attorney-reminder"
                    checked={booking.attorneyReminderEnabled !== false}
                    onCheckedChange={(v) => onToggleReminder(booking, 'attorneyReminderEnabled', v)}
                  />
                </div>
                {booking.phone && (
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <PhoneOutgoing className="h-3.5 w-3.5 text-muted-foreground" />
                        <Label htmlFor="client-confirmation" className="text-sm cursor-pointer">
                          Reminder call to potential client
                        </Label>
                      </div>
                      <Switch
                        id="client-confirmation"
                        checked={booking.clientConfirmationEnabled === true}
                        onCheckedChange={(v) => onToggleReminder(booking, 'clientConfirmationEnabled', v)}
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground ml-6 mt-0.5">
                      AI calls the potential client to confirm — timing set in Settings
                    </p>
                  </div>
                )}
                {booking.reminderSent && (
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Check className="h-3 w-3 text-emerald-500" /> Reminder sent
                  </p>
                )}
                {booking.confirmationSent && (
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Check className="h-3 w-3 text-emerald-500" /> Confirmation call made
                  </p>
                )}
                {/* Test confirmation call — fires the call immediately,
                    bypassing the scheduled timer. Admin-only on both ends.
                    Gated to bookings with a phone number; we send
                    booking.date as the appointment time even when
                    dateResolved is false (the test call's purpose is to
                    validate the prompt; a slightly-off date in the script
                    is acceptable for testing). */}
                {canFireTestCall && booking.phone && (booking.type === 'call' || booking.type === 'chat') && (
                  <div className="pt-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-[11px]"
                      disabled={testCallFiring}
                      onClick={() => setConfirmOpen(true)}
                    >
                      {testCallFiring ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <PhoneCall className="h-3 w-3 mr-1.5" />}
                      Test confirmation call now
                    </Button>
                    <Dialog open={confirmOpen} onOpenChange={(open) => { if (!testCallFiring) setConfirmOpen(open) }}>
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle>Test confirmation call?</DialogTitle>
                          <DialogDescription>
                            This will fire a real outbound call to {formatPhoneDisplay(booking.phone || '')} right now.
                            The potential client will hear your AI agent confirming their booking.
                          </DialogDescription>
                        </DialogHeader>
                        <DialogFooter className="gap-2 sm:gap-2">
                          <Button
                            variant="outline"
                            onClick={() => setConfirmOpen(false)}
                            disabled={testCallFiring}
                          >
                            Cancel
                          </Button>
                          <Button
                            className="bg-brand-royal hover:bg-brand-indigo text-white"
                            disabled={testCallFiring}
                            onClick={() => {
                              setTestCallFiring(true)
                              api.post('/api/voice/bookings/test-confirmation-call', {
                                bookingId: booking.id,
                                bookingType: booking.type,
                                phone: booking.phone,
                                callerName: booking.callerName,
                                appointmentDateISO: booking.date,
                              })
                                .then(() => {
                                  toast({
                                    title: 'Confirmation call initiated',
                                    description: 'Watch your phone — the AI is dialing now.',
                                  })
                                  setConfirmOpen(false)
                                })
                                .catch((err) => {
                                  const msg = err?.response?.data?.message || 'Failed to fire confirmation call'
                                  toast({
                                    title: 'Call failed',
                                    description: msg,
                                    variant: 'destructive',
                                  })
                                })
                                .finally(() => setTestCallFiring(false))
                            }}
                          >
                            {testCallFiring ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <PhoneCall className="h-3 w-3 mr-1.5" />}
                            Fire call
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chat Transcript */}
          {booking.type === 'chat' && booking.conversationId && (
            <ChatTranscript conversationId={booking.conversationId} />
          )}

          {/* Call Transcript — collapsed by default. The "Summary" block at the
              top already shows ai_summary; this is the raw turn-by-turn record. */}
          {booking.type === 'call' && booking.transcript && (
            <CallTranscript transcript={booking.transcript} />
          )}

          {/* Note */}
          {booking.note && (
            <div>
              <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Note</h4>
              <div className="bg-muted/50 rounded-md px-3 py-2">
                <p className="text-sm text-foreground/80 flex items-start gap-1.5">
                  <StickyNote className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                  {booking.note}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions — flex-wrap so 4 buttons don't crowd on narrow phones; pb-safe keeps clear of the iOS home indicator */}
        <div className="border-t px-5 pt-3 pb-3 flex flex-wrap items-center gap-2 [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
          {booking.isExternalCalendarEvent ? (
            booking.externalHtmlLink ? (
              <Button
                size="sm"
                className="bg-brand-royal hover:bg-brand-indigo text-white flex-1 min-w-[140px]"
                onClick={() => window.open(booking.externalHtmlLink, '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Open Calendar Event
              </Button>
            ) : null
          ) : !booking.addressed ? (
            <Button
              size="sm"
              className="bg-brand-royal hover:bg-brand-indigo text-white flex-1 min-w-[140px]"
              onClick={() => onToggleHandled(booking)}
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Mark Handled
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 min-w-[140px]"
              onClick={() => onToggleHandled(booking)}
            >
              <Undo2 className="h-3.5 w-3.5 mr-1.5" />
              Reopen
            </Button>
          )}
          {!booking.isExternalCalendarEvent && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEditNote(booking)}
              >
                <StickyNote className="h-3.5 w-3.5 mr-1.5" />
                {booking.note ? 'Edit Note' : 'Add Note'}
              </Button>
              {booking.phone && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { window.location.href = `tel:${booking.phone}` }}
                >
                  <PhoneCall className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
                onClick={() => onDelete(booking)}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Delete
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

function DetailRow({ icon, label, value }: {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="text-muted-foreground mt-0.5">{icon}</span>
      <div className="min-w-0">
        <p className="text-[11px] text-muted-foreground">{label}</p>
        <p className="text-sm">{value}</p>
      </div>
    </div>
  )
}

function CallTranscript({ transcript }: { transcript: string }) {
  const [expanded, setExpanded] = useState(false)

  // Telnyx end-of-call transcripts arrive as "role: text" lines (see
  // VoiceService.ts where rawTranscript is joined). Render speaker labels
  // (Caller / AI) bold so it skims like a chat. Falls back to plain text
  // for transcripts that don't follow that shape.
  const lines = transcript.split('\n').map(line => {
    const m = line.match(/^(user|assistant|agent|caller|bot)\s*:\s*(.*)$/i)
    if (!m) return { speaker: null, content: line }
    const role = m[1].toLowerCase()
    const isCaller = role === 'user' || role === 'caller'
    return { speaker: isCaller ? 'Caller' : 'AI', content: m[2] }
  })

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 hover:text-foreground transition-colors w-full"
      >
        <PhoneCall className="h-3.5 w-3.5" />
        Call Transcript
        {expanded ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
      </button>
      {expanded && (
        <div className="bg-muted/50 rounded-md px-3 py-2 max-h-64 overflow-y-auto space-y-2">
          {lines.map((line, i) => (
            <div key={i} className={`text-xs ${line.speaker === 'Caller' ? 'text-foreground' : 'text-muted-foreground'}`}>
              {line.speaker && <span className="font-medium">{line.speaker}:</span>}{line.speaker ? ' ' : ''}{line.content}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ChatTranscript({ conversationId }: { conversationId: string }) {
  const [expanded, setExpanded] = useState(false)
  const [messages, setMessages] = useState<Array<{ senderType: string; content: string }>>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (expanded && !loaded) {
      setLoading(true)
      api.get(`/api/agent/conversations/${conversationId}/messages`)
        .then(res => {
          setMessages(res.data.conversation?.messages || [])
          setLoaded(true)
        })
        .catch(() => setMessages([]))
        .finally(() => setLoading(false))
    }
  }, [expanded, conversationId, loaded])

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5 hover:text-foreground transition-colors w-full"
      >
        <MessageSquare className="h-3.5 w-3.5" />
        Chat Transcript
        {expanded ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
      </button>
      {expanded && (
        <div className="bg-muted/50 rounded-md px-3 py-2 max-h-64 overflow-y-auto space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          {!loading && messages.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">No messages found</p>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`text-xs ${msg.senderType === 'visitor' ? 'text-foreground' : 'text-muted-foreground'}`}>
              <span className="font-medium">
                {msg.senderType === 'visitor' ? 'Visitor' : msg.senderType === 'bot' ? 'AI' : 'Agent'}:
              </span>{' '}
              {msg.content}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
